import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Layout
import Layout from './components/layout/Layout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Classes from './pages/Classes';
import Teachers from './pages/Teachers';
import Parents from './pages/Parents';
import Fees from './pages/Fees';
import Exams from './pages/Exams';
import Attendance from './pages/Attendance';
import Reports from './pages/Reports';
import Settings from './pages/Settings';

// Finance Pages
import ChartOfAccounts from './pages/finance/ChartOfAccounts';
import JournalEntries from './pages/finance/JournalEntries';
import GeneralLedger from './pages/finance/GeneralLedger';
import Payroll from './pages/finance/Payroll';
import BalanceSheet from './pages/finance/BalanceSheet';
import ProfitLoss from './pages/finance/ProfitLoss';
import FeeStructures from './pages/finance/FeeStructures';
import Invoices from './pages/finance/Invoices';
import Suppliers from './pages/finance/Suppliers';
import Expenses from './pages/finance/Expenses';
import YearEndClosing from './pages/finance/YearEndClosing';
import SchoolSettings from './pages/finance/SchoolSettings';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students" 
        element={
          <ProtectedRoute>
            <Students />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students/new" 
        element={
          <ProtectedRoute>
            <Students />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes" 
        element={
          <ProtectedRoute>
            <Classes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/teachers" 
        element={
          <ProtectedRoute>
            <Teachers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/parents" 
        element={
          <ProtectedRoute>
            <Parents />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/fees" 
        element={
          <ProtectedRoute>
            <Fees />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/exams" 
        element={
          <ProtectedRoute>
            <Exams />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/attendance" 
        element={
          <ProtectedRoute>
            <Attendance />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />

      {/* Finance Routes (Admin Only) */}
      <Route 
        path="/finance/accounts" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <ChartOfAccounts />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/journals" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <JournalEntries />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/ledger" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <GeneralLedger />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/payroll" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Payroll />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/balance-sheet" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <BalanceSheet />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/profit-loss" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <ProfitLoss />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/fee-structures" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <FeeStructures />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/invoices" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Invoices />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/suppliers" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Suppliers />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/expenses" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <Expenses />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/year-end-closing" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <YearEndClosing />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/school-settings" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <SchoolSettings />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
